import { Answer } from '@osmosis/shared/types/pages/answerPages';

interface PrepareContextArgs {
  title: string;
  rawContext?: string;
  maxLimit?: number;
  bufferSize?: number;
  isVideo?: boolean;
}

export const OAI_PAGE_CONTEXT_LIMIT = 20000;

const TITLE_PREFIX = 'PAGE TITLE: ';
const VIDEO_TITLE_PREFIX = 'PAGE (VIDEO) TITLE: ';
const PAGE_CONTEXT_PREFIX = 'PAGE CONTEXT: ';
const VIDEO_CONTEXT_PREFIX = 'PAGE (VIDEO) CONTEXT: ';

export enum OAI_IFRAME_EVENTS {
  IframeReady = 'SHERPATH_AI_IFRAME_READY',
  PageContextUpdate = 'SHERPATH_AI_PAGE_CONTEXT_UPDATE',
  SubmitUserMessage = 'SHERPATH_AI_SUBMIT_USER_MESSAGE',
  PromptSubmitted = 'SHERPATH_AI_PROMPT_SUBMITTED',
  LearnPageClick = 'OSMOSIS_AI_LEARN_PAGE_CLICK',
}

export enum OAIMode {
  DefaultStudy = 'DEFAULT_STUDY_MODE',
  Tutor = 'TUTOR_MODE',
}

type BuildOsmosisAIUrlArgs = {
  query?: string;
  dashboard?: number | string;
  mode?: string;
};

/**
 * Builds a `/osmosisAI` path with only the params that are actually set.
 */
export const buildOsmosisAIUrl = ({
  query,
  dashboard,
  mode,
}: BuildOsmosisAIUrlArgs): string => {
  const params = new URLSearchParams();
  if (query) params.set('query', query);
  if (dashboard !== undefined) params.set('dashboard', String(dashboard));
  if (mode) params.set('mode', mode);

  const queryString = params.toString().replace(/\+/g, '%20');
  return queryString ? `/osmosisAI?${queryString}` : '/osmosisAI';
};

export type OAIInboundMessage = {
  type: OAI_IFRAME_EVENTS;
  learnUrl?: string; // Only present for LearnPageClick events
};

export type OAIOutboundMessage =
  | {
      type: typeof OAI_IFRAME_EVENTS.PageContextUpdate;
      pageContext: string;
    }
  | {
      type: typeof OAI_IFRAME_EVENTS.SubmitUserMessage;
      data: { message: string; mode: OAIMode };
    };

export type OAIInboundHandlers = Record<
  string,
  (data: OAIInboundMessage) => void
>;

/**
 * Derives the target origin for `postMessage` based on the provided iframeSrc.
 */
export const getOAITargetOrigin = (iframeSrc: string | null): string => {
  if (!iframeSrc) return '*';
  try {
    return new URL(iframeSrc).origin;
  } catch {
    return '*';
  }
};

const LEARN_PATH_PREFIX = '/learn/';

/**
 * Resolves an inbound OSMOSIS_AI_LEARN_PAGE_CLICK message to a same-origin
 * path (+ query) to navigate to, discarding the sender's host entirely.
 * Returns undefined for any other message type or an invalid/non-learn URL.
 */
export const resolveOAILearnPath = (
  message: OAIInboundMessage,
): string | undefined => {
  if (message.type !== OAI_IFRAME_EVENTS.LearnPageClick) return undefined;
  if (!message.learnUrl) return undefined;

  let url: URL;
  try {
    url = new URL(message.learnUrl);
  } catch {
    return undefined;
  }

  if (!url.pathname.startsWith(LEARN_PATH_PREFIX)) return undefined;
  return `${url.pathname}${url.search}`;
};

/**
 * Trims text to `budget` characters, preferring a sentence boundary past 75% of
 * the budget and falling back to the last word boundary.
 */
export const truncateToBudget = (text: string, budget: number): string => {
  if (budget <= 0) return '';
  if (text.length <= budget) return text;

  let chunk = text.slice(0, budget);

  const lastSentenceBoundary = Math.max(
    chunk.lastIndexOf('.'),
    chunk.lastIndexOf('?'),
    chunk.lastIndexOf('!'),
  );

  if (lastSentenceBoundary > budget * 0.75) {
    chunk = chunk.slice(0, lastSentenceBoundary + 1);
  } else {
    const lastSpace = chunk.lastIndexOf(' ');
    if (lastSpace > 0) {
      chunk = chunk.slice(0, lastSpace);
    }
  }

  return chunk.trim();
};

/**
 * Prepares a truncated context string for OAI chat,
 * ensuring that the title and as much of the raw page content as possible are included without exceeding limits.
 */
export const prepareTruncatedContext = ({
  title,
  rawContext = '',
  maxLimit = OAI_PAGE_CONTEXT_LIMIT,
  bufferSize = 0,
  isVideo = false,
}: PrepareContextArgs): string => {
  if (!rawContext)
    return `${isVideo ? VIDEO_TITLE_PREFIX : TITLE_PREFIX}${title.trim()}`;

  // 1. Clean whitespace
  const cleanContext = rawContext.replace(/\s+/g, ' ').trim();

  const prefix = `${isVideo ? VIDEO_TITLE_PREFIX : TITLE_PREFIX}${title.trim()}\n${isVideo ? VIDEO_CONTEXT_PREFIX : PAGE_CONTEXT_PREFIX}`;

  const totalAvailableSpace = maxLimit - bufferSize;

  // 2. Check if everything fits perfectly without any truncation or suffix
  if (prefix.length + cleanContext.length <= totalAvailableSpace) {
    return `${prefix}${cleanContext}`;
  }

  // 3. If we are here, we MUST truncate. Now we factor in the suffix.
  /**
   * PROMPT ENGINEERING NOTE ON THE TRUNCATION SUFFIX:
   * This flag is vital for LLM guardrails. Without it, the AI assumes it has
   * access to the full text and will confidently state "The speaker never
   * mentioned X" (a false negative hallucination).
   * Explicitly warning the AI that data is missing allows it to respond
   * gracefully: "Based on the portion of the context I have access to...".
   */
  const suffix = `\n[Context truncated due to length limits]`;
  const contextBudgetWithSuffix =
    totalAvailableSpace - prefix.length - suffix.length;

  // 4. Return the final prepared context with prefix, truncated context, and suffix
  return `${prefix}${truncateToBudget(cleanContext, contextBudgetWithSuffix)}${suffix}`;
};

/**
 * Utility to convert an array of Q&A pairs into a single context string for OAI, with appropriate formatting and truncation.
 */
export const getContextFromAnswers = (
  title: string,
  answers: Answer[] | undefined,
): string | undefined => {
  if (!answers || !answers.length) {
    return undefined;
  }

  // 1. SERVER-SIDE RUNTIME (or Environments without DOMParser)
  // Using 'typeof' prevents a ReferenceError on the Node.js server
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    const answersContext = answers
      .reduce((acc, { question, answer }) => {
        const answerText = answer
          .replace(/<\/?[^>]+(>|$)/g, '') // Strips HTML tags
          .replace(/&#160;|&nbsp;/g, ' '); // Cleans common spaces

        return `${acc}\n${question?.trim?.()}\n${answerText?.trim?.()}`;
      }, '')
      .trim();

    return prepareTruncatedContext({
      title,
      rawContext: answersContext,
    });
  }

  // 2. CLIENT-SIDE RUNTIME (Browser)
  const parser = new DOMParser();
  const answersContext = answers
    .reduce((acc, { question, answer }) => {
      const answerDoc = parser.parseFromString(answer, 'text/html');
      return `${acc}\n${question?.trim?.() || ''}\n${answerDoc.body.textContent?.trim() || ''}`;
    }, '')
    .trim();

  return prepareTruncatedContext({
    title,
    rawContext: answersContext,
  });
};

/**
 * Utility to convert an HTML string into plain text, stripping tags and cleaning whitespace.
 */
export const parseHtmlToText = (htmlText: string) => {
  if (!htmlText) {
    return undefined;
  }
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return htmlText
      .replace(/<\/?[^>]+(>|$)/g, '')
      .replace(/&#160;|&nbsp;/g, ' ')
      .trim();
  }

  const parser = new DOMParser();
  return parser.parseFromString(htmlText, 'text/html').body.textContent?.trim();
};
